import type { Node, Edge } from '@xyflow/react';
import { MarkerType } from '@xyflow/react';
import type { ArchitectureLane, ArchitectureLegendItem, ArchitectureNodeData } from './ArchitectureGraph.js';

// ==========================================
// Shared edge defaults
// Edge colours are theme variables: ArchitectureGraph draws plain SVG, which resolves `var()` per theme.
// ==========================================

const ACCENT = 'var(--color-accent)';
const SUBTLE = 'var(--color-faint)';

const animatedEdge = {
  animated: true,
  style: { stroke: ACCENT, strokeWidth: 2 },
  markerEnd: { type: MarkerType.ArrowClosed, color: ACCENT, width: 16, height: 16 },
};

const subtleEdge = {
  animated: false,
  style: { stroke: SUBTLE, strokeWidth: 1.5 },
  markerEnd: { type: MarkerType.ArrowClosed, color: SUBTLE, width: 14, height: 14 },
};

const dottedEdge = {
  animated: false,
  style: { stroke: SUBTLE, strokeWidth: 1.5, strokeDasharray: '6 4' },
  markerEnd: { type: MarkerType.ArrowClosed, color: SUBTLE, width: 14, height: 14 },
};

const defaultLegend: ArchitectureLegendItem[] = [
  { label: 'Interface', tone: 'frontend' },
  { label: 'Runtime', tone: 'backend' },
  { label: 'Data', tone: 'database' },
  { label: 'Actor/Provider', tone: 'cloud' },
  { label: 'Safety', tone: 'security' },
  { label: 'Transport', tone: 'bus' },
];

// ==========================================
// View definitions
// ==========================================

export interface ViewDefinition {
  id: string;
  title: string;
  description: string;
  nodes: Node<ArchitectureNodeData>[];
  edges: Edge[];
  lanes?: ArchitectureLane[];
  legend?: ArchitectureLegendItem[];
  /** When set, the tab renders supplemental markdown under the graph, or alone for text-only tabs. */
  markdown?: string;
}

export const VIEWS: ViewDefinition[] = [
  // ────────────────── Overview ──────────────────
  {
    id: 'overview',
    title: 'System Overview',
    description:
      'Client surfaces call the server API. The engine owns autonomous orchestration. PostgreSQL is durable state; Redis carries runtime commands, worker capability signals, and streams.',
    legend: defaultLegend,
    nodes: [
      // Client layer (y=40)
      {
        id: 'client',
        position: { x: 80, y: 40 },
        type: 'system',
        data: {
          category: 'Client',
          label: 'React Dashboard',
          description: 'Vite + React user surfaces for projects, workflows, workforce, help, and admin operations.',
        },
      },
      {
        id: 'wb',
        position: { x: 380, y: 40 },
        type: 'small',
        data: {
          label: 'Workflow Builder',
          description:
            'Authoring cockpit for workflow steps, contracts, versions, processors, debug output, and Action Request repair paths.',
        },
      },
      {
        id: 'intel-ui',
        position: { x: 560, y: 40 },
        type: 'small',
        data: {
          label: 'Intel Sources UI',
          description:
            'Knowledge and catalog discovery views for source-backed data, candidates, and accumulated intelligence.',
        },
      },
      {
        id: 'tmpl-ui',
        position: { x: 740, y: 40 },
        type: 'small',
        data: {
          label: 'Template Editor',
          description:
            'Capability authoring surface for specialist personas, tools, model requirements, permissions, and evaluation metadata.',
        },
      },
      // Server layer (y=220)
      {
        id: 'server',
        position: { x: 80, y: 220 },
        type: 'system',
        data: {
          category: 'Server',
          label: 'HTTP API',
          description:
            'Client-facing API gate for auth, RBAC, REST/SSE/OpenAPI surfaces, and durable read/write state.',
        },
      },
      {
        id: 'gateway',
        position: { x: 420, y: 220 },
        type: 'process',
        data: {
          category: 'AI',
          label: 'AI Gateway',
          description:
            'Model routing and provider abstraction for LLM-backed assistant, workflow, task, and workforce execution.',
        },
      },
      {
        id: 'stream',
        position: { x: 720, y: 220 },
        type: 'process',
        data: {
          category: 'Streaming',
          label: 'Agent Streaming',
          description:
            'Server-to-client event stream for assistant turns, workflow observations, and runtime progress.',
        },
      },
      // Engine layer (y=420)
      {
        id: 'engine',
        position: { x: 80, y: 420 },
        type: 'system',
        data: {
          category: 'Engine',
          label: 'Background Engine',
          description:
            'Autonomous orchestrator for workflow commands, schedules, worker dispatch, daemon decisions, and runtime interpretation.',
        },
      },
      {
        id: 'workflow-eng',
        position: { x: 420, y: 420 },
        type: 'process',
        data: {
          category: 'Core',
          label: 'Workflow Engine',
          description:
            'Step orchestration through Conflui runtime interfaces, keeping current adapters default until live parity decisions.',
        },
      },
      {
        id: 'scheduler',
        position: { x: 720, y: 420 },
        type: 'process',
        data: {
          category: 'Core',
          label: 'Scheduler',
          description: 'Schedules future workflow and daemon commands without replaying missed starts.',
        },
      },
      // Data layer (y=620)
      {
        id: 'pg',
        position: { x: 180, y: 620 },
        type: 'data',
        data: {
          category: 'Database',
          label: 'PostgreSQL',
          description:
            'Durable org-scoped domain state, audits, versions, resources, projects, workflows, tasks, and Action Requests.',
        },
      },
      {
        id: 'redis',
        position: { x: 540, y: 620 },
        type: 'data',
        data: {
          category: 'Cache',
          label: 'Redis',
          description: 'Runtime command/stream transport plus worker heartbeat and capability availability signal.',
        },
      },
      // LLM providers (right side)
      {
        id: 'ollama',
        position: { x: 1000, y: 220 },
        type: 'small',
        data: { label: 'Ollama', description: 'Local model provider option used through the model routing layer.' },
      },
      {
        id: 'anthropic',
        position: { x: 1000, y: 310 },
        type: 'small',
        data: { label: 'Anthropic', description: 'Hosted model provider option used through the AI gateway.' },
      },
      {
        id: 'openai',
        position: { x: 1000, y: 400 },
        type: 'small',
        data: {
          label: 'OpenAI',
          description: 'Hosted model provider and selective handoff adapter target for OpenAI-backed paths.',
        },
      },
      {
        id: 'nvidia',
        position: { x: 1000, y: 490 },
        type: 'small',
        data: {
          label: 'NVIDIA NIM',
          description: 'Hosted or enterprise model endpoint option behind the provider gateway.',
        },
      },
    ],
    edges: [
      { id: 'e-client-server', source: 'client', target: 'server', ...animatedEdge },
      { id: 'e-wb-client', source: 'wb', target: 'client', ...subtleEdge },
      { id: 'e-intel-client', source: 'intel-ui', target: 'client', ...subtleEdge },
      { id: 'e-tmpl-client', source: 'tmpl-ui', target: 'client', ...subtleEdge },
      { id: 'e-server-gw', source: 'server', target: 'gateway', ...animatedEdge },
      { id: 'e-stream-gw', source: 'stream', target: 'gateway', ...subtleEdge },
      { id: 'e-server-pg', source: 'server', target: 'pg', ...animatedEdge },
      { id: 'e-server-redis', source: 'server', target: 'redis', ...animatedEdge },
      { id: 'e-redis-engine', source: 'redis', target: 'engine', ...animatedEdge },
      { id: 'e-engine-pg', source: 'engine', target: 'pg', ...animatedEdge },
      { id: 'e-engine-we', source: 'engine', target: 'workflow-eng', ...subtleEdge },
      { id: 'e-engine-sched', source: 'engine', target: 'scheduler', ...subtleEdge },
      { id: 'e-gw-ollama', source: 'gateway', target: 'ollama', ...dottedEdge },
      { id: 'e-gw-anthropic', source: 'gateway', target: 'anthropic', ...dottedEdge },
      { id: 'e-gw-openai', source: 'gateway', target: 'openai', ...dottedEdge },
      { id: 'e-gw-nvidia', source: 'gateway', target: 'nvidia', ...dottedEdge },
    ],
  },

  // ────────────────── Workflow Pipeline ──────────────────
  {
    id: 'workflow-pipeline',
    title: 'Workflow Pipeline',
    description:
      'A trigger starts a workflow, the orchestrator resolves dependencies, worker availability is checked, steps run through processors or agents, and durable outputs are routed through the output factory.',
    legend: defaultLegend,
    nodes: [
      {
        id: 'trigger',
        position: { x: 40, y: 200 },
        type: 'system',
        data: {
          category: 'Trigger',
          label: 'Manual / Schedule / Event',
          description: 'User action, future schedule, event, task, daemon, or project context requests a workflow run.',
        },
      },
      {
        id: 'dag',
        position: { x: 320, y: 200 },
        type: 'process',
        data: {
          category: 'Engine',
          label: 'Workflow Orchestrator',
          description:
            'Resolves step order, state transitions, retries, cancellation, and audit through runtime adapters.',
        },
      },
      {
        id: 'type-check',
        position: { x: 580, y: 200 },
        type: 'process',
        data: {
          category: 'Router',
          label: 'Step Type?',
          description: 'Routes deterministic processors, LLM/template steps, tools, human gates, and task spawns.',
        },
      },
      {
        id: 'processor',
        position: { x: 860, y: 80 },
        type: 'process',
        data: {
          category: 'Deterministic',
          label: 'Processor Step',
          description:
            'Executes predictable merge, filter, aggregate, normalize, route, dispatch, or field-map logic without LLM calls.',
        },
      },
      {
        id: 'agent',
        position: { x: 860, y: 320 },
        type: 'system',
        data: {
          category: 'AI',
          label: 'Agent Step',
          description: 'Runs a template-backed LLM step with tool policy, contracts, retry feedback, and audit.',
        },
      },
      {
        id: 'tools',
        position: { x: 1120, y: 320 },
        type: 'small',
        data: {
          label: 'Tool-Use Loop',
          description:
            'Worker executes allowed tool, MCP, browser, container, or LLM envelopes and returns structured events.',
        },
      },
      {
        id: 'parse',
        position: { x: 1120, y: 420 },
        type: 'small',
        data: {
          label: 'Parse Output',
          description: 'Normalizes step output into structured result parts before contract validation.',
        },
      },
      {
        id: 'verify',
        position: { x: 1120, y: 510 },
        type: 'small',
        data: {
          label: 'Contract Check',
          description: 'Validates runtime output against the workflow step contract and records pass/fail metadata.',
        },
      },
      {
        id: 'router',
        position: { x: 1120, y: 140 },
        type: 'process',
        data: {
          category: 'Output',
          label: 'Output Processor',
          description:
            'Turns WorkOutput and OutputParts into artifacts, decisions, tasks, dashboard state, notifications, or external delivery.',
        },
      },
      {
        id: 'ch-signal',
        position: { x: 1400, y: 40 },
        type: 'small',
        data: {
          label: 'Diagnostic Event',
          description: 'Records runtime observations such as no capable worker, contract drift, or dispatch warnings.',
        },
      },
      {
        id: 'ch-metric',
        position: { x: 1400, y: 120 },
        type: 'small',
        data: {
          label: 'Metric',
          description: 'Captures scores, latency, cost, pass rates, and evaluation measurements.',
        },
      },
      {
        id: 'ch-summary',
        position: { x: 1400, y: 200 },
        type: 'small',
        data: {
          label: 'Summary',
          description: 'Stores plain-language result summaries for command centers and detail pages.',
        },
      },
      {
        id: 'ch-report',
        position: { x: 1400, y: 280 },
        type: 'small',
        data: {
          label: 'Artifact / Report',
          description: 'Creates durable artifacts, reports, findings, and generated documents.',
        },
      },
      {
        id: 'ch-dash',
        position: { x: 1400, y: 360 },
        type: 'small',
        data: {
          label: 'Dashboard',
          description: 'Updates read-model surfaces and project/workflow command center panels.',
        },
      },
      {
        id: 'ch-notif',
        position: { x: 1400, y: 440 },
        type: 'small',
        data: {
          label: 'Notification',
          description: 'Sends notification-ready events or webhooks after durable state is recorded.',
        },
      },
    ],
    edges: [
      { id: 'e-trig-dag', source: 'trigger', target: 'dag', ...animatedEdge },
      { id: 'e-dag-type', source: 'dag', target: 'type-check', ...animatedEdge },
      { id: 'e-type-proc', source: 'type-check', target: 'processor', ...subtleEdge },
      { id: 'e-type-agent', source: 'type-check', target: 'agent', ...animatedEdge },
      { id: 'e-agent-tools', source: 'agent', target: 'tools', ...animatedEdge },
      { id: 'e-tools-parse', source: 'tools', target: 'parse', ...subtleEdge },
      { id: 'e-parse-verify', source: 'parse', target: 'verify', ...subtleEdge },
      { id: 'e-verify-router', source: 'verify', target: 'router', ...animatedEdge },
      { id: 'e-proc-router', source: 'processor', target: 'router', ...subtleEdge },
      { id: 'e-r-sig', source: 'router', target: 'ch-signal', ...subtleEdge },
      { id: 'e-r-met', source: 'router', target: 'ch-metric', ...subtleEdge },
      { id: 'e-r-sum', source: 'router', target: 'ch-summary', ...subtleEdge },
      { id: 'e-r-rep', source: 'router', target: 'ch-report', ...subtleEdge },
      { id: 'e-r-dash', source: 'router', target: 'ch-dash', ...subtleEdge },
      { id: 'e-r-not', source: 'router', target: 'ch-notif', ...subtleEdge },
    ],
  },

  // ────────────────── Agent Execution ──────────────────
  {
    id: 'agent-execution',
    title: 'Agent Execution',
    description:
      'Agent execution assembles template, contracts, tools, context, and safety policy, then runs through the AgentRunner adapter and worker envelopes until a final structured output is produced.',
    legend: defaultLegend,
    nodes: [
      // Column 1: Input (x=40)
      {
        id: 'input',
        position: { x: 40, y: 240 },
        type: 'process',
        data: {
          category: 'Input',
          label: 'Step Input',
          description:
            'Selected workflow/task/conversation data plus contracts, examples, and relevant source context.',
        },
      },
      // Column 2: Skills (x=300)
      {
        id: 'resolver',
        position: { x: 300, y: 80 },
        type: 'process',
        data: {
          category: 'Registry',
          label: 'Tool Resolver',
          description:
            'Selects eligible tools, MCP calls, skills, and adapters from template, task, workflow, and policy context.',
        },
      },
      {
        id: 'exec-skills',
        position: { x: 300, y: 240 },
        type: 'small',
        data: {
          label: 'Executable Tools',
          description: 'Callable tools and MCP/OpenAPI operations that may execute through worker-owned envelopes.',
        },
      },
      {
        id: 'inst-skills',
        position: { x: 300, y: 340 },
        type: 'small',
        data: {
          label: 'Instructional Skills',
          description: 'Procedural memory injected into the prompt without direct side effects.',
        },
      },
      // Column 3: Prompt assembly (x=560)
      {
        id: 'role',
        position: { x: 560, y: 40 },
        type: 'small',
        data: {
          label: 'Role Prompt',
          description: 'Runtime source and persona guidance for the actor executing this turn.',
        },
      },
      {
        id: 'template',
        position: { x: 560, y: 140 },
        type: 'small',
        data: {
          label: 'Template Version',
          description:
            'Pinned capability definition containing goal, instructions, constraints, tools, permissions, and quality metadata.',
        },
      },
      {
        id: 'override',
        position: { x: 560, y: 240 },
        type: 'small',
        data: {
          label: 'Step Override',
          description: 'Workflow or task-specific instructions and effective input/output contracts.',
        },
      },
      {
        id: 'ctx-rules',
        position: { x: 560, y: 340 },
        type: 'small',
        data: {
          label: 'Context Rules',
          description: 'Redaction, source selection, memory, artifact, and evidence rules for this execution boundary.',
        },
      },
      // Column 4: LLM (x=840)
      {
        id: 'llm',
        position: { x: 840, y: 160 },
        type: 'system',
        data: {
          category: 'AI',
          label: 'LLM Provider',
          description:
            'Model router chooses an eligible provider based on capability, policy, budget, and runtime adapter support.',
        },
      },
      {
        id: 'resp-check',
        position: { x: 840, y: 380 },
        type: 'process',
        data: {
          category: 'Check',
          label: 'Response Type?',
          description:
            'Classifies whether the model produced final output, a tool call, a repair attempt, or invalid structured data.',
        },
      },
      // Column 5: Tool execution (x=1120)
      {
        id: 'hooks',
        position: { x: 1120, y: 160 },
        type: 'process',
        data: {
          category: 'Safety',
          label: 'Pre-Hooks',
          description:
            'Checks permissions, budget, risk, data boundary, rate limits, and tool policy before execution.',
        },
      },
      {
        id: 'adapter',
        position: { x: 1120, y: 340 },
        type: 'process',
        data: {
          category: 'Router',
          label: 'Tool Adapter',
          description:
            'Routes calls to built-in, MCP, OpenAPI, browser, container, repo snapshot, or other worker executors.',
        },
      },
      {
        id: 'result',
        position: { x: 1120, y: 40 },
        type: 'small',
        data: {
          label: 'Tool Result',
          description: 'Structured observation returned to the agent loop with audit and redaction applied.',
        },
      },
      // Output (bottom)
      {
        id: 'done',
        position: { x: 560, y: 480 },
        type: 'process',
        data: {
          category: 'Output',
          label: 'Agent Output',
          description:
            'Final structured response, artifact candidate, decision proposal, memory candidate, or task/workflow output part.',
        },
      },
    ],
    edges: [
      { id: 'e-in-res', source: 'input', target: 'resolver', ...animatedEdge },
      { id: 'e-res-exec', source: 'resolver', target: 'exec-skills', ...subtleEdge },
      { id: 'e-res-inst', source: 'resolver', target: 'inst-skills', ...subtleEdge },
      { id: 'e-role-llm', source: 'role', target: 'llm', ...subtleEdge },
      { id: 'e-tmpl-llm', source: 'template', target: 'llm', ...subtleEdge },
      { id: 'e-ovr-llm', source: 'override', target: 'llm', ...subtleEdge },
      { id: 'e-ctx-llm', source: 'ctx-rules', target: 'llm', ...subtleEdge },
      { id: 'e-exec-llm', source: 'exec-skills', target: 'llm', ...dottedEdge },
      { id: 'e-inst-llm', source: 'inst-skills', target: 'llm', ...dottedEdge },
      { id: 'e-llm-resp', source: 'llm', target: 'resp-check', ...animatedEdge },
      { id: 'e-resp-done', source: 'resp-check', target: 'done', ...animatedEdge },
      { id: 'e-resp-hooks', source: 'resp-check', target: 'hooks', ...animatedEdge },
      { id: 'e-hooks-adapter', source: 'hooks', target: 'adapter', ...subtleEdge },
      { id: 'e-adapter-result', source: 'adapter', target: 'result', ...animatedEdge },
      { id: 'e-result-llm', source: 'result', target: 'llm', ...animatedEdge },
    ],
  },

  // ────────────────── Tool And Skill Architecture ──────────────────
  {
    id: 'skill-architecture',
    title: 'Tool And Skill Architecture',
    description:
      'Tools, MCP operations, OpenAPI calls, user-defined actions, and instructional skills are unified through a registry and executed only through policy-aware adapters.',
    legend: defaultLegend,
    nodes: [
      // Column 1: Sources (x=40)
      {
        id: 'src-builtin',
        position: { x: 40, y: 40 },
        type: 'small',
        data: {
          label: 'Built-in (TypeScript)',
          description:
            'First-party deterministic functions and server/engine capabilities exposed through strict contracts.',
        },
      },
      {
        id: 'src-mcp',
        position: { x: 40, y: 140 },
        type: 'small',
        data: {
          label: 'MCP Servers',
          description: 'External or internal Model Context Protocol servers with audited tool definitions.',
        },
      },
      {
        id: 'src-openapi',
        position: { x: 40, y: 240 },
        type: 'small',
        data: {
          label: 'OpenAPI Specs',
          description: 'HTTP APIs imported as callable operations with request/response schemas.',
        },
      },
      {
        id: 'src-user',
        position: { x: 40, y: 340 },
        type: 'small',
        data: {
          label: 'User-Defined',
          description: 'Organization-defined actions and tool wrappers governed by permissions and review.',
        },
      },
      {
        id: 'src-lib',
        position: { x: 40, y: 440 },
        type: 'small',
        data: {
          label: 'Skill Libraries',
          description: 'Reusable procedural instructions and promoted library capabilities.',
        },
      },
      // Column 2: Adapters (x=340)
      {
        id: 'a-builtin',
        position: { x: 340, y: 40 },
        type: 'process',
        data: {
          category: 'Adapter',
          label: 'BuiltInAdapter',
          description: 'Executes first-party functions with typed input/output and runtime audit.',
        },
      },
      {
        id: 'a-mcp',
        position: { x: 340, y: 140 },
        type: 'process',
        data: {
          category: 'Adapter',
          label: 'McpAdapter',
          description: 'Calls MCP tools after definition validation, policy checks, and poisoning-risk review.',
        },
      },
      {
        id: 'a-openapi',
        position: { x: 340, y: 240 },
        type: 'process',
        data: {
          category: 'Adapter',
          label: 'OpenApiAdapter',
          description: 'Executes HTTP operations with schema validation, auth boundary checks, and redaction.',
        },
      },
      {
        id: 'a-user',
        position: { x: 340, y: 340 },
        type: 'process',
        data: {
          category: 'Adapter',
          label: 'UserDefinedAdapter',
          description: 'Runs org-defined actions only inside allowed execution and approval boundaries.',
        },
      },
      {
        id: 'a-lib',
        position: { x: 340, y: 440 },
        type: 'process',
        data: {
          category: 'Adapter',
          label: 'SkillLibraryAdapter',
          description: 'Injects reviewed procedural memory into prompts without direct side effects.',
        },
      },
      // Column 3: Registry (x=680)
      {
        id: 'registry',
        position: { x: 680, y: 200 },
        type: 'system',
        data: {
          category: 'Core',
          label: 'Tool Registry',
          description: 'Unified discovery, compatibility, policy, execution, and quality metadata layer.',
        },
      },
      // Column 4: API (x=980)
      {
        id: 'reg-exec',
        position: { x: 980, y: 80 },
        type: 'small',
        data: {
          label: 'execute()',
          description: 'Runs an allowed tool through the selected adapter and returns a structured observation.',
        },
      },
      {
        id: 'reg-resolve',
        position: { x: 980, y: 180 },
        type: 'small',
        data: {
          label: 'resolveForStep()',
          description: 'Finds compatible tools for a workflow step, task, template, or workforce role.',
        },
      },
      {
        id: 'reg-record',
        position: { x: 980, y: 280 },
        type: 'small',
        data: {
          label: 'recordExecution()',
          description: 'Persists audit evidence, redacted payload metadata, errors, and cost/latency facts.',
        },
      },
      {
        id: 'reg-quality',
        position: { x: 980, y: 380 },
        type: 'small',
        data: {
          label: 'updateQualityScore()',
          description: 'Feeds evaluation and reliability signals back into capability selection.',
        },
      },
    ],
    edges: [
      { id: 'e-s1-a1', source: 'src-builtin', target: 'a-builtin', ...subtleEdge },
      { id: 'e-s2-a2', source: 'src-mcp', target: 'a-mcp', ...subtleEdge },
      { id: 'e-s3-a3', source: 'src-openapi', target: 'a-openapi', ...subtleEdge },
      { id: 'e-s4-a4', source: 'src-user', target: 'a-user', ...subtleEdge },
      { id: 'e-s5-a5', source: 'src-lib', target: 'a-lib', ...subtleEdge },
      { id: 'e-a1-reg', source: 'a-builtin', target: 'registry', ...animatedEdge },
      { id: 'e-a2-reg', source: 'a-mcp', target: 'registry', ...animatedEdge },
      { id: 'e-a3-reg', source: 'a-openapi', target: 'registry', ...animatedEdge },
      { id: 'e-a4-reg', source: 'a-user', target: 'registry', ...animatedEdge },
      { id: 'e-a5-reg', source: 'a-lib', target: 'registry', ...animatedEdge },
      { id: 'e-reg-exec', source: 'registry', target: 'reg-exec', ...subtleEdge },
      { id: 'e-reg-resolve', source: 'registry', target: 'reg-resolve', ...subtleEdge },
      { id: 'e-reg-record', source: 'registry', target: 'reg-record', ...subtleEdge },
      { id: 'e-reg-quality', source: 'registry', target: 'reg-quality', ...subtleEdge },
    ],
  },

  // ────────────────── Intel Sources ──────────────────
  {
    id: 'intel-sources',
    title: 'Intel Sources',
    description:
      'Workflows and catalog daemons discover source-backed entities, store evidence and relationships, and expose accumulated intelligence to users, agents, and review workflows.',
    legend: defaultLegend,
    nodes: [
      // Column 1: Data sources (x=40)
      {
        id: 'yt',
        position: { x: 40, y: 40 },
        type: 'small',
        data: { label: 'YouTube', description: 'Video/channel source identities and extracted content fragments.' },
      },
      {
        id: 'tw',
        position: { x: 40, y: 120 },
        type: 'small',
        data: { label: 'Twitter / X', description: 'Social profile, post, and relationship sources where configured.' },
      },
      {
        id: 'wallets',
        position: { x: 40, y: 200 },
        type: 'small',
        data: { label: 'Wallets', description: 'Blockchain addresses and related transaction/context evidence.' },
      },
      {
        id: 'defi',
        position: { x: 40, y: 300 },
        type: 'small',
        data: {
          label: 'DeFi Protocols',
          description: 'Protocol, pool, and integration sources for finance-oriented intelligence.',
        },
      },
      {
        id: 'news',
        position: { x: 40, y: 380 },
        type: 'small',
        data: {
          label: 'News Feeds',
          description: 'External articles, feeds, publications, and documents with stable source identity.',
        },
      },
      {
        id: 'exchanges',
        position: { x: 40, y: 460 },
        type: 'small',
        data: {
          label: 'Exchanges',
          description: 'Market, instrument, and venue sources used by finance module workflows.',
        },
      },
      // Column 2: Ingest (x=300)
      {
        id: 'discovery',
        position: { x: 300, y: 100 },
        type: 'process',
        data: {
          category: 'Ingest',
          label: 'Discovery',
          description: 'Finds candidate sources, canonical identities, and source fragments for later review.',
        },
      },
      {
        id: 'fetch',
        position: { x: 300, y: 340 },
        type: 'process',
        data: {
          category: 'Ingest',
          label: 'Content Fetch',
          description: 'Fetches source content with freshness policy, provenance capture, and extractor routing.',
        },
      },
      // Column 3: Storage (x=580)
      {
        id: 'sources-db',
        position: { x: 580, y: 60 },
        type: 'data',
        data: {
          category: 'Graph',
          label: 'intel_sources',
          description: 'Canonical source identities, scores, freshness, and source-backed metadata.',
        },
      },
      {
        id: 'data-db',
        position: { x: 580, y: 240 },
        type: 'data',
        data: {
          category: 'Graph',
          label: 'intel_source_data',
          description: 'Fetched content, metrics, fragments, and evidence payloads.',
        },
      },
      {
        id: 'network-db',
        position: { x: 580, y: 420 },
        type: 'data',
        data: {
          category: 'Graph',
          label: 'intel_source_network',
          description: 'Edges between sources, entities, candidates, and discovered relationships.',
        },
      },
      // Column 4: Views (x=880)
      {
        id: 'catalog',
        position: { x: 880, y: 60 },
        type: 'process',
        data: {
          category: 'Read',
          label: 'Entity Catalog',
          description: 'Browse, filter, and inspect source-backed entities before promotion or use.',
        },
      },
      {
        id: 'agent-q',
        position: { x: 880, y: 240 },
        type: 'process',
        data: {
          category: 'Read',
          label: 'Inline Agent',
          description: 'Ask context-aware questions grounded in accumulated source evidence.',
        },
      },
      {
        id: 'detail',
        position: { x: 880, y: 420 },
        type: 'process',
        data: {
          category: 'Read',
          label: 'Source Detail',
          description: 'Shows source timeline, extracted evidence, network relationships, and freshness.',
        },
      },
    ],
    edges: [
      { id: 'e-yt-disc', source: 'yt', target: 'discovery', ...subtleEdge },
      { id: 'e-tw-disc', source: 'tw', target: 'discovery', ...subtleEdge },
      { id: 'e-wal-disc', source: 'wallets', target: 'discovery', ...subtleEdge },
      { id: 'e-defi-fetch', source: 'defi', target: 'fetch', ...subtleEdge },
      { id: 'e-news-fetch', source: 'news', target: 'fetch', ...subtleEdge },
      { id: 'e-ex-fetch', source: 'exchanges', target: 'fetch', ...subtleEdge },
      { id: 'e-disc-src', source: 'discovery', target: 'sources-db', ...animatedEdge },
      { id: 'e-disc-net', source: 'discovery', target: 'network-db', ...animatedEdge },
      { id: 'e-fetch-data', source: 'fetch', target: 'data-db', ...animatedEdge },
      { id: 'e-src-cat', source: 'sources-db', target: 'catalog', ...subtleEdge },
      { id: 'e-data-aq', source: 'data-db', target: 'agent-q', ...subtleEdge },
      { id: 'e-net-det', source: 'network-db', target: 'detail', ...subtleEdge },
    ],
  },

  // ────────────────── Safety Pipeline ──────────────────
  {
    id: 'safety-pipeline',
    title: 'Safety Pipeline',
    description:
      'Every tool or worker action passes through policy, budget, data-boundary, and execution-envelope checks before it runs. Blocks and results are audited.',
    legend: defaultLegend,
    nodes: [
      // Linear pipeline left-to-right
      {
        id: 'call',
        position: { x: 40, y: 160 },
        type: 'system',
        data: {
          category: 'Input',
          label: 'Tool Call',
          description: 'An agent, workflow step, task, or workforce member requests an executable capability.',
        },
      },
      {
        id: 'h-perm',
        position: { x: 300, y: 160 },
        type: 'process',
        data: {
          category: 'Hook 1',
          label: 'Permission Check',
          description: 'Verifies organization policy, role grants, permission tier, and requested operation scope.',
        },
      },
      {
        id: 'h-guard',
        position: { x: 540, y: 160 },
        type: 'process',
        data: {
          category: 'Hook 2',
          label: 'Tool Guardian',
          description:
            'Checks tool definition, prompt-injection risk, MCP/OpenAPI trust, and dangerous operation patterns.',
        },
      },
      {
        id: 'h-rate',
        position: { x: 780, y: 160 },
        type: 'process',
        data: {
          category: 'Hook 3',
          label: 'Rate Limit',
          description: 'Applies actor, organization, API key, and runtime-specific rate ceilings.',
        },
      },
      {
        id: 'h-cost',
        position: { x: 1020, y: 160 },
        type: 'process',
        data: {
          category: 'Hook 4',
          label: 'Cost Guard',
          description: 'Checks token, spend, time, and per-run budget before dispatching work.',
        },
      },
      {
        id: 'h-exec',
        position: { x: 1260, y: 160 },
        type: 'process',
        data: {
          category: 'Hook 5',
          label: 'Execution Guard',
          description:
            'Builds a worker execution envelope for containers, MCP, browser, repo snapshot, or external calls.',
        },
      },
      {
        id: 'h-content',
        position: { x: 1500, y: 160 },
        type: 'process',
        data: {
          category: 'Hook 6',
          label: 'Content Guard',
          description: 'Applies redaction and data-boundary checks before sending content to providers or tools.',
        },
      },
      {
        id: 'execute',
        position: { x: 1740, y: 160 },
        type: 'system',
        data: {
          category: 'Execute',
          label: 'Run Capability',
          description: 'Allowed work is executed by the worker package; business interpretation stays in the engine.',
        },
      },
      // Bottom row
      {
        id: 'audit',
        position: { x: 780, y: 380 },
        type: 'data',
        data: {
          category: 'Logging',
          label: 'Audit Trail',
          description: 'Records blocks, approvals, execution envelopes, redacted metadata, and output status.',
        },
      },
      {
        id: 'post-hook',
        position: { x: 1500, y: 380 },
        type: 'process',
        data: {
          category: 'Post',
          label: 'Post-Hook',
          description: 'Captures quality, reliability, cost, and evaluation signals after execution.',
        },
      },
    ],
    edges: [
      { id: 'e-call-perm', source: 'call', target: 'h-perm', ...animatedEdge },
      { id: 'e-perm-guard', source: 'h-perm', target: 'h-guard', ...animatedEdge },
      { id: 'e-guard-rate', source: 'h-guard', target: 'h-rate', ...animatedEdge },
      { id: 'e-rate-cost', source: 'h-rate', target: 'h-cost', ...animatedEdge },
      { id: 'e-cost-exec-g', source: 'h-cost', target: 'h-exec', ...animatedEdge },
      { id: 'e-exec-g-content', source: 'h-exec', target: 'h-content', ...animatedEdge },
      { id: 'e-content-execute', source: 'h-content', target: 'execute', ...animatedEdge },
      { id: 'e-execute-post', source: 'execute', target: 'post-hook', ...subtleEdge },
      { id: 'e-post-audit', source: 'post-hook', target: 'audit', ...subtleEdge },
      { id: 'e-perm-audit', source: 'h-perm', target: 'audit', ...dottedEdge },
      { id: 'e-guard-audit', source: 'h-guard', target: 'audit', ...dottedEdge },
      { id: 'e-rate-audit', source: 'h-rate', target: 'audit', ...dottedEdge },
      { id: 'e-cost-audit', source: 'h-cost', target: 'audit', ...dottedEdge },
      { id: 'e-exec-audit', source: 'h-exec', target: 'audit', ...dottedEdge },
      { id: 'e-content-audit', source: 'h-content', target: 'audit', ...dottedEdge },
    ],
  },

  // ────────────────── Workforce ──────────────────
  {
    id: 'daemon-workforce',
    title: 'Workforce / Daemons',
    description:
      'Workforce employees are org-scoped actors for customer work. Daemons are system-scoped maintenance actors. Both run through engine-owned workflows, governance, and audit.',
    legend: defaultLegend,
    nodes: [
      // Column 1: Trigger sources (x=40)
      {
        id: 'cron',
        position: { x: 40, y: 40 },
        type: 'small',
        data: {
          label: 'Cron Schedule',
          description: 'Future scheduled runs are dispatched only when policy and worker availability allow execution.',
        },
      },
      {
        id: 'event-trigger',
        position: { x: 40, y: 120 },
        type: 'small',
        data: {
          label: 'Event Trigger',
          description:
            'Domain events, findings, catalog updates, Action Requests, or workflow outcomes can request actor work.',
        },
      },
      {
        id: 'chained',
        position: { x: 40, y: 200 },
        type: 'small',
        data: {
          label: 'Chained Trigger',
          description: 'One actor or workflow can request another reviewed workflow or specialist route.',
        },
      },
      // Column 2: Event bus (x=280)
      {
        id: 'event-bus',
        position: { x: 280, y: 100 },
        type: 'data',
        data: {
          category: 'Infrastructure',
          label: 'Event Bus',
          description:
            'Runtime transport for actor events, workflow commands, diagnostics, and worker availability signals.',
        },
      },
      // Column 3: Dispatch layer (x=540)
      {
        id: 'scheduler',
        position: { x: 540, y: 40 },
        type: 'process',
        data: {
          category: 'Scheduler',
          label: 'Actor Scheduler',
          description: 'Matches scheduled daemon/workforce triggers and emits commands only after policy checks.',
        },
      },
      {
        id: 'dispatcher',
        position: { x: 540, y: 180 },
        type: 'process',
        data: {
          category: 'Dispatcher',
          label: 'Event Dispatcher',
          description: 'Matches events to enabled triggers, workforce routes, or daemon workflows.',
        },
      },
      // Column 4: Execution (x=820)
      {
        id: 'runner',
        position: { x: 820, y: 40 },
        type: 'system',
        data: {
          category: 'Core',
          label: 'Actor Runner',
          description:
            'Runs workforce and daemon work through governance, workflow orchestration, worker dispatch, and reflection.',
        },
      },
      {
        id: 'governance',
        position: { x: 820, y: 200 },
        type: 'process',
        data: {
          category: 'Safety',
          label: 'Governance Gate',
          description:
            'Checks enabled state, approval requirements, permission tier, rate limits, budget, and worker capability availability.',
        },
      },
      // Column 5: Workflow (x=1100)
      {
        id: 'workflow',
        position: { x: 1100, y: 40 },
        type: 'process',
        data: {
          category: 'Execution',
          label: 'Workflow Engine',
          description:
            'Explicit orchestration path for actor collaboration, review chains, findings, reports, and upgrade proposals.',
        },
      },
      // Column 6: Outputs (x=1380)
      {
        id: 'findings',
        position: { x: 1380, y: 40 },
        type: 'data',
        data: {
          category: 'Output',
          label: 'Findings',
          description: 'Source-backed findings, quality/security issues, upgrade candidates, and recommendations.',
        },
      },
      {
        id: 'reflection',
        position: { x: 1380, y: 180 },
        type: 'data',
        data: {
          category: 'Output',
          label: 'Reflection',
          description: 'Actor self-assessment, quality rating, confidence, limitations, and next-action proposal.',
        },
      },
      {
        id: 'supervisor',
        position: { x: 1380, y: 320 },
        type: 'process',
        data: {
          category: 'Supervision',
          label: 'Supervisor / Review',
          description: 'Human or daemon review validates output quality, security, and adoption readiness.',
        },
      },
    ],
    edges: [
      { id: 'e-cron-sched', source: 'cron', target: 'scheduler', ...animatedEdge },
      { id: 'e-event-bus', source: 'event-trigger', target: 'event-bus', ...animatedEdge },
      { id: 'e-bus-dispatch', source: 'event-bus', target: 'dispatcher', ...animatedEdge },
      { id: 'e-chain-bus', source: 'chained', target: 'event-bus', ...subtleEdge },
      { id: 'e-sched-runner', source: 'scheduler', target: 'runner', ...animatedEdge },
      { id: 'e-dispatch-runner', source: 'dispatcher', target: 'runner', ...animatedEdge },
      { id: 'e-runner-gov', source: 'runner', target: 'governance', ...animatedEdge },
      { id: 'e-runner-wf', source: 'runner', target: 'workflow', ...animatedEdge },
      { id: 'e-wf-findings', source: 'workflow', target: 'findings', ...animatedEdge },
      { id: 'e-runner-reflect', source: 'runner', target: 'reflection', ...subtleEdge },
      { id: 'e-runner-bus', source: 'runner', target: 'event-bus', ...dottedEdge },
      { id: 'e-supervisor', source: 'reflection', target: 'supervisor', ...subtleEdge },
    ],
  },

  // ────────────────── Worker SDK ──────────────────
  {
    id: 'worker-sdk',
    title: 'Worker SDK',
    description:
      'External systems invoke org-scoped agent templates through authenticated HTTP, then observe terminal state through polling, webhook delivery, or WebSocket event streams.',
    legend: defaultLegend,
    nodes: [
      {
        id: 'external-client',
        position: { x: 40, y: 160 },
        type: 'system',
        data: {
          category: 'Client',
          label: 'External Client',
          description: 'Script, service, integration, or partner application using a ck_* API key.',
        },
      },
      {
        id: 'worker-api',
        position: { x: 300, y: 160 },
        type: 'system',
        data: {
          category: 'Server',
          label: 'Worker API',
          description:
            'REST and WebSocket endpoints that accept run requests, follow-ups, cancellation, polling, and stream subscription.',
        },
      },
      {
        id: 'api-key-policy',
        position: { x: 300, y: 320 },
        type: 'process',
        data: {
          category: 'Safety',
          label: 'API Key Policy',
          description:
            'Validates bearer token scope, tier, quota, idempotency key, and organization boundary before enqueueing work.',
        },
      },
      {
        id: 'command-stream',
        position: { x: 580, y: 160 },
        type: 'data',
        data: {
          category: 'Transport',
          label: 'Redis Command Stream',
          description: 'Durable runtime command handoff from the server gate to the engine command handler.',
        },
      },
      {
        id: 'engine-handler',
        position: { x: 860, y: 160 },
        type: 'process',
        data: {
          category: 'Engine',
          label: 'Command Handler',
          description:
            'Engine-owned handler claims worker commands, starts template execution, and records lifecycle state.',
        },
      },
      {
        id: 'template-run',
        position: { x: 1140, y: 160 },
        type: 'process',
        data: {
          category: 'Runtime',
          label: 'Template Run',
          description:
            'Runs the requested agent template through runtime adapters, worker envelopes, tool policy, and output validation.',
        },
      },
      {
        id: 'event-stream',
        position: { x: 1140, y: 340 },
        type: 'data',
        data: {
          category: 'Transport',
          label: 'Run Event Stream',
          description:
            'Stores state changes, observations, terminal events, and delivery payloads for the external run.',
        },
      },
      {
        id: 'polling',
        position: { x: 1420, y: 60 },
        type: 'small',
        data: {
          label: 'Status Polling',
          description: 'External client reads run status and final result from the Worker API.',
        },
      },
      {
        id: 'webhook',
        position: { x: 1420, y: 180 },
        type: 'small',
        data: {
          label: 'Webhook Delivery',
          description: 'Terminal-state POST with HMAC signature when webhookUrl is configured.',
        },
      },
      {
        id: 'ws',
        position: { x: 1420, y: 300 },
        type: 'small',
        data: { label: 'WebSocket Push', description: 'Live run events delivered to subscribed external clients.' },
      },
    ],
    edges: [
      { id: 'e-client-api', source: 'external-client', target: 'worker-api', ...animatedEdge },
      { id: 'e-api-policy', source: 'worker-api', target: 'api-key-policy', ...subtleEdge },
      { id: 'e-policy-stream', source: 'api-key-policy', target: 'command-stream', ...animatedEdge },
      { id: 'e-stream-engine', source: 'command-stream', target: 'engine-handler', ...animatedEdge },
      { id: 'e-engine-template', source: 'engine-handler', target: 'template-run', ...animatedEdge },
      { id: 'e-template-events', source: 'template-run', target: 'event-stream', ...animatedEdge },
      { id: 'e-api-polling', source: 'worker-api', target: 'polling', ...subtleEdge },
      { id: 'e-events-webhook', source: 'event-stream', target: 'webhook', ...subtleEdge },
      { id: 'e-events-ws', source: 'event-stream', target: 'ws', ...subtleEdge },
      { id: 'e-poll-client', source: 'polling', target: 'external-client', ...dottedEdge },
      { id: 'e-ws-client', source: 'ws', target: 'external-client', ...dottedEdge },
    ],
    markdown: `## Worker SDK — Architecture

The Worker SDK exposes a black-box transport layer so external systems can invoke Conflui agent templates without knowledge of the internal engine.

\`\`\`mermaid
graph LR
    Client["External Client\\n(script / service)"] -->|"POST /api/worker/v1/runs\\nBearer ck_*"| Server["Conflui Server"]
    Server -->|XADD worker:run| Redis["Redis Streams\\nconfluent:commands"]
    Redis -->|XREAD| Engine["Engine\\nWorker Command Handler"]
    Engine -->|runAgentTemplate| Template["Agent Template"]
    Template -->|events| EngineOut["XADD worker:stream:<id>"]
    EngineOut -->|webhook POST| Webhook["Your Webhook\\nEndpoint"]
    EngineOut -->|WebSocket push| WS["WS /api/worker/v1/ws/<id>"]
    Client -->|GET /api/worker/v1/runs/<id>| Server
\`\`\`

## Authentication

All Worker SDK endpoints require a \`ck_*\` Bearer token issued from **Settings → API Keys**:

\`\`\`
Authorization: Bearer ck_live_abc123xyz...
\`\`\`

Keys carry a **scope**, **tier** (standard / high / enterprise), and **daily token quota**.

## Run Lifecycle

\`\`\`mermaid
stateDiagram-v2
    [*] --> Accepted: POST /v1/runs → 202
    Accepted --> Running: Engine picks up worker:run command
    Running --> Running: Tool calls & LLM turns
    Running --> Completed: Agent finished
    Running --> Failed: Error or timeout
    Running --> Cancelled: DELETE /v1/runs/{id}
    Completed --> [*]
    Failed --> [*]
    Cancelled --> [*]
\`\`\`

## HTTP Endpoints

| Method | Path | Purpose |
|---|---|---|
| \`POST\` | \`/api/worker/v1/runs\` | Create a run (202 + stub) |
| \`GET\` | \`/api/worker/v1/runs/{runId}\` | Poll run status |
| \`DELETE\` | \`/api/worker/v1/runs/{runId}\` | Cancel a run (204) |
| \`POST\` | \`/api/worker/v1/runs/{runId}/follow-up\` | Append a follow-up turn (202) |

## Rate Limits

| Tier | Requests/min | Daily tokens |
|---|---|---|
| standard | 60 | 1,000,000 |
| high | 600 | configurable |
| enterprise | unlimited | configurable |

Rate-limited requests receive **429** with a \`Retry-After\` header.

## Webhook Delivery

When \`webhookUrl\` is provided, the server delivers a POST to that URL on terminal state (\`completed\`, \`failed\`, \`cancelled\`). Verify authenticity with the \`X-Confluent-Signature\` HMAC-SHA256 header using \`verifyWebhook()\` from the Worker SDK package.

## Idempotency

Pass \`idempotencyKey\` in the request body. Duplicate calls within 24 h per API key return the existing run stub — no duplicate execution.

---

*This tab is the operator overview. Endpoint-level details are tracked in generated OpenAPI and Worker SDK package documentation.*
`,
  },
];
